import { type RefObject, useLayoutEffect, useState } from 'react';

import { scrollingAncestor } from '@/util/scroll';

interface Options {
    /** One row's height, the register's 68. */
    rowHeight: number;
    /** What sits under the rows inside the same viewport: the footer bar, the card's padding, the page's padding. */
    reserve: number;
    min?: number;
    max?: number;
    /** Below this viewport width the rows stack and vary in height, so the count is fixed instead. */
    fixedBelow?: number;
    fixed?: number;
}

/** What a caller shows while the measurement has not happened yet. */
export const ROWS_BEFORE_MEASURE = 8;

/**
 * How many rows of a register fit between the top of `ref` and the bottom of the viewport, so a
 * desk sees a register that ends where the screen does instead of a page that scrolls. Measured
 * on mount and on every resize; on a phone the rows are cards of varying height, so the count
 * falls back to a fixed number and the page scrolls as phones do. The floor is the prototype's
 * ten rows: a short laptop window scrolls a little rather than paging four rows at a time.
 *
 * Null until the first measurement rather than a guess that is corrected a moment later, because
 * a register whose server pages asks for exactly this many rows: handing it a number to fetch
 * before anything has been measured costs a request that is thrown away.
 */
export function useFittedRows(
    ref: RefObject<HTMLElement | null>,
    {
        rowHeight,
        reserve,
        min = 10,
        max = 30,
        fixedBelow = 768,
        fixed = ROWS_BEFORE_MEASURE,
    }: Options,
): number | null {
    const [rows, setRows] = useState<number | null>(null);

    useLayoutEffect(() => {
        const measure = () => {
            const element = ref.current;
            if (!element || window.innerWidth < fixedBelow) {
                setRows(fixed);
                return;
            }
            if (!element.getClientRects().length) {
                // Nothing laid out to measure: the element is detached, hidden, or in an
                // environment that does no layout at all. Keep a measurement already taken rather
                // than clobbering it, but do settle on the fallback if there is none, so a caller
                // waiting for the first size is never left waiting for ever.
                setRows((current) => current ?? fixed);
                return;
            }
            // Where the element sits with the page scrolled to its top, not where it sits now.
            // The observer below re-measures whenever the register changes size, an opened row
            // for one, and measured against a scrolled page the room grows by the scroll, which
            // asked the server for a bigger page and collapsed the row (ALS-58).
            const top = element.getBoundingClientRect().top + scrollingAncestor(element).scrollTop;
            const room = window.innerHeight - top - reserve;
            setRows(Math.min(max, Math.max(min, Math.floor(room / rowHeight))));
        };
        measure();
        window.addEventListener('resize', measure);
        const observer = new ResizeObserver(measure);
        if (ref.current?.parentElement) observer.observe(ref.current.parentElement);
        return () => {
            window.removeEventListener('resize', measure);
            observer.disconnect();
        };
    }, [ref, rowHeight, reserve, min, max, fixedBelow, fixed]);

    return rows;
}
